import { MonitoringKanBanStatusEnum, MonitorTypeEnum } from '@/app/common/general-types/social-screening.dto';
import {
  daysBetween,
  formatMonitoringDay,
  fromIsoDate,
  startOfLocalDay,
} from '@/app/pages/monitoring/molecules/create-monitoring-brief/sections/monitoring-window/monitoring-window.helpers';

export type MonitorTab = 'overview' | 'brief_management' | 'content' | 'creators' | 'results';

export const MONITOR_TABS: readonly { id: MonitorTab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'brief_management', label: 'Brief management' },
  { id: 'content', label: 'Content' },
  { id: 'creators', label: 'Creators' },
  { id: 'results', label: 'Results' },
];

export interface MonitorTabCounts {
  postsFound: number | null;
  creatorCount: number | null;
  resultsCount: number | null;
}

export interface MonitorTabView {
  id: MonitorTab;
  label: string;
  count?: number;
  countNoun?: string;
}

/** Content, Creators, and Results show a count once it has loaded. Zero is a real count. Overview and Brief management never have one. */
export function monitorTabs(counts: MonitorTabCounts): MonitorTabView[] {
  return MONITOR_TABS.map((tab) => {
    if (tab.id === 'content' && counts.postsFound !== null) {
      return { id: tab.id, label: tab.label, count: counts.postsFound, countNoun: 'posts' };
    }
    if (tab.id === 'creators' && counts.creatorCount !== null) {
      return { id: tab.id, label: tab.label, count: counts.creatorCount, countNoun: 'creators' };
    }
    if (tab.id === 'results' && counts.resultsCount !== null) {
      return { id: tab.id, label: tab.label, count: counts.resultsCount, countNoun: 'posts' };
    }
    return { id: tab.id, label: tab.label };
  });
}

const TAB_ALIASES: Record<string, MonitorTab> = {
  brief: 'brief_management',
  briefing: 'brief_management',
  performance: 'results',
  sentiment: 'brief_management',
  tracker: 'brief_management',
};

const VALID_TABS = new Set<string>(MONITOR_TABS.map((tab) => tab.id));

/** A brief opens on Overview. Old Briefing, Sentiment, and Tracker links open Brief management. Performance opens Results. */
export function parseMonitorTab(raw: string | null | undefined): MonitorTab {
  if (!raw) {
    return 'overview';
  }
  const aliased = TAB_ALIASES[raw] ?? raw;
  return VALID_TABS.has(aliased) ? (aliased as MonitorTab) : 'overview';
}

export function isMonitorTab(raw: string): raw is MonitorTab {
  return VALID_TABS.has(raw);
}

export function statusLabel(status: number | null | undefined): string {
  switch (status) {
    case MonitoringKanBanStatusEnum.Pre_Creators:
      return 'No creators';
    case MonitoringKanBanStatusEnum.Not_Ready:
      return 'Not ready';
    case MonitoringKanBanStatusEnum.Processing:
      return 'Processing';
    case MonitoringKanBanStatusEnum.Refinement:
      return 'Needs review';
    case MonitoringKanBanStatusEnum.Monitoring:
      return 'Monitoring';
    case MonitoringKanBanStatusEnum.Complete:
      return 'Complete';
    default:
      return 'Monitoring brief';
  }
}

/** Days until the stored window end. The end date itself, and every day after it, reads Ended. */
export function daysLeftLabel(until: Date | string | null | undefined, today: Date): string {
  const end = parseDay(until);
  if (!end) {
    return '—';
  }
  const days = daysBetween(startOfLocalDay(today), end);
  return days <= 0 ? 'Ended' : String(days);
}

export interface MonitorBriefFacts {
  briefText?: string | null;
  contentRequirements?: string | null;
  contentDonts?: string | null;
  briefUrl?: string | null;
  createdDate?: Date | string | null;
  monitorStart?: Date | string | null;
  monitorUntil?: Date | string | null;
  monitorType?: string | null;
  product?: { productName?: string | null; productImage?: string | null } | null;
  complianceDocIds?: string[] | null;
  currentMonitoringKanbanProgress?: { status?: number | null; updatedBy?: string | null } | null;
}

export interface ComplianceFact {
  id: string;
  complianceName?: string | null;
  complianceDocumentUrl?: string | null;
}

export interface BriefManagementView {
  briefText: string | null;
  requirements: string | null;
  restrictions: string | null;
  documentUrl: string | null;
  createdLabel: string | null;
  postedBy: string | null;
  productName: string | null;
  productImage: string | null;
  monitorType: string;
  compliance: { id: string; name: string; url?: string }[];
  window: { from: string; to: string; days: number } | null;
}

export function briefManagementView(
  brief: MonitorBriefFacts,
  postedBy: string | null,
  compliance: ComplianceFact[],
): BriefManagementView {
  const start = parseDay(brief.monitorStart);
  const end = parseDay(brief.monitorUntil);
  const created = parseDay(brief.createdDate);

  return {
    briefText: presentText(brief.briefText),
    requirements: presentText(brief.contentRequirements),
    restrictions: presentText(brief.contentDonts),
    documentUrl: presentText(brief.briefUrl),
    createdLabel: created ? formatMonitoringDay(created) : null,
    postedBy: presentText(postedBy),
    productName: presentText(brief.product?.productName),
    productImage: presentText(brief.product?.productImage),
    monitorType: monitorTypeLabel(brief.monitorType),
    compliance: compliance.flatMap((doc) => {
      if (!doc.id) {
        return [];
      }
      const item: { id: string; name: string; url?: string } = {
        id: doc.id,
        name: presentText(doc.complianceName) ?? 'Compliance document',
      };
      const url = presentText(doc.complianceDocumentUrl);
      if (url) {
        item.url = url;
      }
      return [item];
    }),
    window:
      start && end
        ? { from: formatMonitoringDay(start), to: formatMonitoringDay(end), days: daysBetween(start, end) }
        : null,
  };
}

export function monitorTypeLabel(type: string | null | undefined): string {
  return type === MonitorTypeEnum.CREATOR_MONITOR_ALL ? 'Monitor Brand Safety' : 'Find Brief Content';
}

/** Find Brief Content briefs only surface matching posts; Shout doesn't score them against the brief's rules. */
export function isFindBriefContent(type: string | null | undefined): boolean {
  return type !== MonitorTypeEnum.CREATOR_MONITOR_ALL;
}

function presentText(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function parseDay(value: Date | string | null | undefined): Date | null {
  if (value == null || value === '') {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : startOfLocalDay(value);
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return fromIsoDate(value);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : startOfLocalDay(parsed);
}
